import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import EmailQuotaBanner from "./EmailQuotaBanner";
import EmailQuotaSummaryCard from "./EmailQuotaSummaryCard";
import {
  fetchEmailQuotaHistory,
  fetchEmailQuotaStatus,
  reconcileEmailQuota,
  updateEmailQuotaConfig,
  type EmailQuotaBanner as BannerPayload,
  type EmailQuotaHistoryItem,
  type EmailQuotaStatus,
} from "../../api/emailQuotaApi";

const EmailQuotaPanel = () => {
  const [status, setStatus] = useState<EmailQuotaStatus | null>(null);
  const [banner, setBanner] = useState<BannerPayload | null>(null);
  const [history, setHistory] = useState<EmailQuotaHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mailjetStarterActive, setMailjetStarterActive] = useState(false);
  const [overflowToMailjet, setOverflowToMailjet] = useState(false);
  const [alertToInput, setAlertToInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [quota, hist] = await Promise.all([
        fetchEmailQuotaStatus(),
        fetchEmailQuotaHistory(30),
      ]);
      setStatus(quota.status);
      setBanner(quota.banner);
      setHistory(hist);
      setMailjetStarterActive(Boolean(quota.status.mailjetPaidConfirmedAt));
      setOverflowToMailjet(Boolean(quota.status.overflowToMailjet));
      setAlertToInput((quota.status.config?.alertTo || []).join(", "));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kon quota-status niet laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await updateEmailQuotaConfig({
        mailjetStarterActive,
        overflowToMailjet: mailjetStarterActive ? overflowToMailjet : false,
        alertTo: alertToInput
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      toast.success("Quota-configuratie opgeslagen");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleReconcile = async () => {
    try {
      await reconcileEmailQuota();
      toast.success("Reconciliatie voltooid");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Reconciliatie mislukt");
    }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Resend quota</h1>
        <p className="text-gray-600">
          Interne caps (95/dag, 2850/maand UTC), waarschuwingen en optionele Mailjet-overflow.
        </p>
      </div>

      <EmailQuotaBanner banner={banner} />

      <EmailQuotaSummaryCard compact linkable={false} />

      <Card>
        <CardHeader>
          <CardTitle>Mailjet Starter & overflow</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="mailjet-starter">Mailjet Starter actief</Label>
              <p className="text-xs text-muted-foreground">
                Bevestig dat Starter ($9/maand) in Mailjet Console is geactiveerd.
              </p>
            </div>
            <Switch
              id="mailjet-starter"
              checked={mailjetStarterActive}
              onCheckedChange={setMailjetStarterActive}
              disabled={loading || saving}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="overflow-mailjet">Overflow naar Mailjet bij Resend-cap</Label>
              <p className="text-xs text-muted-foreground">
                Alleen mogelijk na Mailjet Starter-bevestiging.
              </p>
            </div>
            <Switch
              id="overflow-mailjet"
              checked={overflowToMailjet}
              onCheckedChange={setOverflowToMailjet}
              disabled={!mailjetStarterActive || loading || saving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="alert-to">Alert-e-mailadressen (comma-separated)</Label>
            <Input
              id="alert-to"
              value={alertToInput}
              onChange={(e) => setAlertToInput(e.target.value)}
              placeholder="ops@labwick.com"
              disabled={loading || saving}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveConfig} disabled={loading || saving}>
              {saving ? "Opslaan…" : "Opslaan"}
            </Button>
            <Button type="button" variant="outline" onClick={() => void load()} disabled={loading}>
              Vernieuwen
            </Button>
            <Button type="button" variant="secondary" onClick={() => void handleReconcile()} disabled={loading}>
              Reconciliatie
            </Button>
          </div>
        </CardContent>
      </Card>

      {history.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Dagverbruik (30 dagen, UTC)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto text-sm">
              {history.map((row) => (
                <div key={row.period} className="flex justify-between border-b py-1">
                  <span>{row.period}</span>
                  <span>
                    {row.used} / {row.hardCap}
                    {row.driftDetected ? " · drift" : ""}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default EmailQuotaPanel;
